import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip as MuiTooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { FaSearch, FaInfoCircle, FaBan } from "react-icons/fa";
import { BsChevronDown, BsChevronExpand, BsChevronUp } from "react-icons/bs";
import { useQuery } from "react-query";
import { Column, useSortBy, useTable } from "react-table";
import { useHistory } from "react-router-dom";

import { AuthContext } from "../../contexts/auth";
import DashboardService from "../../services/dashboard.service";
import {
  BudgetDashboardFiltersDto,
  BudgetDashboardProjectItem,
  BudgetDashboardSortField,
  SortOrder,
} from "../../types/dashboard";
import { isProjectAdmin } from "../../utils/resolveClosingControl";
import { AdoptionByCellChart } from "./Components/AdoptionByCellChart";
import { AdoptionEvolutionChart } from "./Components/AdoptionEvolutionChart";
import { AdoptionKpis } from "./Components/AdoptionKpis";
import { InsightsPanel } from "./Components/InsightsPanel";
import { StaleProjectsCard } from "./Components/StaleProjectsCard";
import { StatusMultiSelect } from "./Components/StatusMultiSelect";
import { TopClientsChart } from "./Components/TopClientsChart";

const sortFieldMap: Record<string, BudgetDashboardSortField> = {
  mmCode: BudgetDashboardSortField.MM_CODE,
  clientName: BudgetDashboardSortField.CLIENT_NAME,
  cellName: BudgetDashboardSortField.CELL_NAME,
  date: BudgetDashboardSortField.DATE,
  statusName: BudgetDashboardSortField.STATUS,
  budgetCount: BudgetDashboardSortField.BUDGET_COUNT,
};

const formatDate = (value?: string) => {
  if (!value) return "---";

  return new Intl.DateTimeFormat("pt-BR").format(new Date(value));
};

const getDefaultDates = () => {
  return {
    startDate: "",
    endDate: "",
  };
};

const getInitialDates = () => {
  const today = new Date();
  const tenDaysAgo = new Date();
  tenDaysAgo.setDate(today.getDate() - 10);

  const pad = (n: number) => n.toString().padStart(2, "0");

  const formatDateToISO = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  return {
    startDate: formatDateToISO(tenDaysAgo),
    endDate: formatDateToISO(today),
  };
};

const getStatusColor = (status?: string) => {
  const normalized = (status || "").toLowerCase();

  if (normalized.includes("faturado") || normalized.includes("billed")) {
    return { color: "#166534", backgroundColor: "#DCFCE7" };
  }

  if (normalized.includes("cancel") || normalized.includes("canceled")) {
    return { color: "#991B1B", backgroundColor: "#FEE2E2" };
  }

  if (normalized.includes("aprovação") || normalized.includes("aproval")) {
    return { color: "#92400E", backgroundColor: "#FEF3C7" };
  }

  if (normalized.includes("aprovado") || normalized.includes("approved")) {
    return { color: "#1D4ED8", backgroundColor: "#DBEAFE" };
  }

  return { color: "#475569", backgroundColor: "#E2E8F0" };
};

const formatDateRange = (startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return null;

  const parseISODate = (iso?: string) => {
    if (!iso) return null;
    const parts = iso.split("-").map(Number);
    if (parts.length < 3) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const start = parseISODate(startDate);
  const end = parseISODate(endDate);
  if (!start || !end) return null;

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const formatDate = (date: Date) => {
    const day = date.getDate().toString().padStart(2, "0");
    const month = new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(
      date
    );
    const year = date.getFullYear();
    return `${day} ${month.charAt(0).toUpperCase() + month.slice(1)} ${year}`;
  };

  return {
    days: diffDays,
    startFormatted: formatDate(start),
    endFormatted: formatDate(end),
  };
};

export const DashboardOrcamentos = () => {
  const history = useHistory();
  const { user, userRoleName } = useContext(AuthContext);
  // O atalho da Blacklist é restrito a administradores
  const isAdmin = isProjectAdmin(userRoleName, user?.role_id);
  const [filters, setFilters] = useState<BudgetDashboardFiltersDto>(
    getDefaultDates()
  );
  const [hasUserModifiedDates, setHasUserModifiedDates] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  // Filtro do card "sem orçamento há +N dias": vale apenas para a tabela,
  // os indicadores continuam refletindo o período inteiro.
  const [staleOnly, setStaleOnly] = useState(false);
  const [sortBy, setSortBy] = useState<BudgetDashboardSortField>(
    BudgetDashboardSortField.MM_CODE
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const [allProjects, setAllProjects] = useState<BudgetDashboardProjectItem[]>(
    []
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [projectsError, setProjectsError] = useState<Error | null>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
    setAllProjects([]);
  }, [filters, debouncedSearchTerm, sortBy, sortOrder, staleOnly]);

  useEffect(() => {
    if (isFirstLoad && !hasUserModifiedDates) {
      const initialDates = getInitialDates();
      setFilters((current) => ({
        ...current,
        startDate: initialDates.startDate,
        endDate: initialDates.endDate,
      }));
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, hasUserModifiedDates]);

  const tableFilters = useMemo(
    () => ({
      ...filters,
      searchQuery: debouncedSearchTerm || undefined,
      staleOnly: staleOnly || undefined,
      sortBy,
      sortOrder,
      page,
      pageSize: rowsPerPage,
    }),
    [debouncedSearchTerm, filters, page, sortBy, sortOrder, staleOnly]
  );

  const { data: options, isLoading: loadingOptions } = useQuery(
    ["budget-dashboard-options"],
    () => DashboardService.getBudgetDashboardOptions(),
    { refetchOnWindowFocus: false }
  );

  const { data: summary, isLoading: loadingSummary } = useQuery(
    ["budget-dashboard-summary", filters],
    () => DashboardService.getBudgetDashboardSummary(filters),
    { refetchOnWindowFocus: false }
  );

  const { data: monthlyData = [], isLoading: loadingMonthly } = useQuery(
    ["budget-dashboard-monthly", filters],
    () => DashboardService.getBudgetDashboardMonthly(filters),
    { refetchOnWindowFocus: false }
  );

  const { data: cellData = [], isLoading: loadingCells } = useQuery(
    ["budget-dashboard-by-cell", filters],
    () => DashboardService.getBudgetDashboardByCell(filters),
    { refetchOnWindowFocus: false }
  );

  const { data: clientData = [], isLoading: loadingClients } = useQuery(
    ["budget-dashboard-by-client", filters],
    () => DashboardService.getBudgetDashboardByClient(filters),
    { refetchOnWindowFocus: false }
  );

  const {
    data: projects,
    isFetching: loadingProjects,
    error: projectsQueryError,
  } = useQuery(
    ["budget-dashboard-projects", tableFilters],
    () => DashboardService.getBudgetDashboardProjects(tableFilters),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!projects?.data) return;

    if (page === 0) {
      setAllProjects(projects.data);
    } else if (isLoadingMore && projects.data.length > 0) {
      setAllProjects((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newItems = projects.data.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newItems];
      });
    }

    setIsLoadingMore(false);
    setProjectsError(null);
  }, [projects?.data, page, isLoadingMore]);

  useEffect(() => {
    if (!projectsQueryError) return;

    setProjectsError(projectsQueryError as Error);
    setIsLoadingMore(false);
  }, [projectsQueryError]);

  const pageItems = allProjects;
  const totalRows = projects?.total ?? 0;
  const hasMore = pageItems.length < totalRows;
  const isInitialProjectsLoading =
    loadingProjects && page === 0 && pageItems.length === 0;
  const isLoadingNextPage = loadingProjects && page > 0;
  const hasProjectsError = !!projectsError;

  useEffect(() => {
    if (!loaderRef.current || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingProjects &&
          !isLoadingMore &&
          !hasProjectsError
        ) {
          setIsLoadingMore(true);
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, root: containerRef.current }
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [hasMore, loadingProjects, isLoadingMore, pageItems.length, totalRows]);

  const maxBudgetCount = Math.max(
    ...pageItems.map((item) => item.budgetCount),
    1
  );

  const columns = useMemo<Column<BudgetDashboardProjectItem>[]>(
    () => [
      {
        Header: "MM Code",
        id: "mmCode",
        accessor: "mmCode",
        Cell: ({ row }: any) => (
          <strong>{`${row.original.mmCode}/${row.original.yearCode}`}</strong>
        ),
      },
      {
        Header: "Cliente",
        id: "clientName",
        accessor: "clientName",
        Cell: ({ value }: any) => <span>{value || "---"}</span>,
      },
      {
        Header: "Célula",
        id: "cellName",
        accessor: "cellName",
        Cell: ({ value }: any) => <span>{value || "---"}</span>,
      },
      {
        Header: "Data",
        id: "date",
        accessor: "date",
        Cell: ({ row, value }: any) => (
          <Box display="flex" alignItems="center" gap={0.75}>
            <span>{formatDate(value)}</span>
            {row.original.daysSinceCreation !== null && (
              <Typography component="span" fontSize={12} color="#94A3B8">
                ({row.original.daysSinceCreation}d)
              </Typography>
            )}
          </Box>
        ),
      },
      {
        Header: "Status MM",
        id: "statusName",
        accessor: "statusName",
        Cell: ({ row }: any) => {
          const label =
            row.original.statusName || row.original.statusCode || "---";
          const colors = getStatusColor(label);

          return (
            <Chip
              label={label}
              size="small"
              sx={{
                fontWeight: 700,
                color: colors.color,
                backgroundColor: colors.backgroundColor,
              }}
            />
          );
        },
      },
      {
        Header: "Orçamento",
        id: "hasBudget",
        accessor: "hasBudget",
        disableSortBy: true,
        Cell: ({ row }: any) => {
          const { hasBudget, isStale } = row.original;

          const chip = hasBudget
            ? { label: "Sim", color: "#166534", backgroundColor: "#DCFCE7" }
            : isStale
            ? {
                label: `Não · +${summary?.staleDays ?? 7}d`,
                color: "#9A3412",
                backgroundColor: "#FFEDD5",
              }
            : { label: "Não", color: "#991B1B", backgroundColor: "#FEE2E2" };

          return (
            <Chip
              label={chip.label}
              size="small"
              sx={{
                fontWeight: 700,
                color: chip.color,
                backgroundColor: chip.backgroundColor,
              }}
            />
          );
        },
      },
      {
        Header: "Qtd. Orçamentos",
        id: "budgetCount",
        accessor: "budgetCount",
        Cell: ({ value }: any) => {
          const width = `${Math.round(
            (Number(value) / maxBudgetCount) * 100
          )}%`;

          return (
            <Box display="flex" alignItems="center" gap={1.5} minWidth={160}>
              <Typography fontWeight={700} minWidth={24}>
                {value}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: "#E2E8F0",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: Number(value) === 0 ? 0 : width,
                    height: "100%",
                    backgroundColor:
                      Number(value) === 0 ? "#CBD5E1" : "#6366F1",
                  }}
                />
              </Box>
            </Box>
          );
        },
      },
    ],
    [maxBudgetCount, summary?.staleDays]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy: tableSortBy },
  } = useTable(
    {
      columns,
      data: pageItems,
      initialState: { sortBy: [{ id: "mmCode", desc: false }] },
      manualSortBy: true,
      autoResetSortBy: false,
      disableSortRemove: true,
    },
    useSortBy
  );

  useEffect(() => {
    if (!tableSortBy?.length) return;

    const column = tableSortBy[0];
    const nextSortBy =
      sortFieldMap[column.id] || BudgetDashboardSortField.MM_CODE;

    setSortBy(nextSortBy);
    setSortOrder(column.desc ? SortOrder.DESC : SortOrder.ASC);
  }, [tableSortBy]);

  const updateFilter = (field: keyof BudgetDashboardFiltersDto, value: any) => {
    if (field === "startDate" || field === "endDate") {
      setHasUserModifiedDates(true);
    }

    const isEmpty = Array.isArray(value) ? value.length === 0 : !value;

    setFilters((current) => ({
      ...current,
      [field]: isEmpty ? undefined : value,
    }));
  };

  const resetFilters = () => {
    setFilters(getDefaultDates());
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setStaleOnly(false);
    setSortBy(BudgetDashboardSortField.MM_CODE);
    setSortOrder(SortOrder.ASC);
    setPage(0);
    setAllProjects([]);
    setIsLoadingMore(false);
    setProjectsError(null);
    setHasUserModifiedDates(false);
  };

  const dateRangeInfo = useMemo(
    () => formatDateRange(filters.startDate, filters.endDate),
    [filters.startDate, filters.endDate]
  );

  const SortIcon = ({ column }: { column: any }) => {
    if (column.disableSortBy) return null;
    if (!column.isSorted) return <BsChevronExpand />;
    return column.isSortedDesc ? <BsChevronDown /> : <BsChevronUp />;
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F9FCFF",
        height: "100%",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 3,
          px: 3,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Projetos x Orçamentos
            </Typography>
            <Typography color="#64748B">
              Acompanhe a evolução mensal e o status dos orçamentos por projeto.
            </Typography>
          </Box>
          {isAdmin && (
            <MuiTooltip title="Gerenciar Blacklist de Clientes">
              <IconButton
                onClick={() => history.push("/dashboard/orcamentos/blacklist")}
                sx={{ color: "#64748B" }}
              >
                <FaBan />
              </IconButton>
            </MuiTooltip>
          )}
        </Box>

        <Card sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Cliente</InputLabel>
                <Select
                  label="Cliente"
                  value={filters.clientId || ""}
                  onChange={(event) =>
                    updateFilter("clientId", event.target.value)
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {options?.clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Célula</InputLabel>
                <Select
                  label="Célula"
                  value={filters.cellId || ""}
                  onChange={(event) =>
                    updateFilter("cellId", event.target.value)
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {options?.cells.map((cell) => (
                    <MenuItem key={cell.id} value={cell.id}>
                      {cell.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2.4}>
              <StatusMultiSelect
                options={options?.statuses ?? []}
                value={filters.status ?? []}
                onChange={(value) => updateFilter("status", value)}
              />
            </Grid>
            <Grid item xs={12} md={1.8}>
              <TextField
                fullWidth
                size="small"
                label="Data início"
                type="date"
                value={filters.startDate || ""}
                onChange={(event) =>
                  updateFilter("startDate", event.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1.8}>
              <TextField
                fullWidth
                size="small"
                label="Data fim"
                type="date"
                value={filters.endDate || ""}
                onChange={(event) =>
                  updateFilter("endDate", event.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1.2}>
              <Button fullWidth variant="outlined" onClick={resetFilters}>
                Limpar
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={!!filters.onlyWithBudget}
                  onChange={(event) =>
                    updateFilter("onlyWithBudget", event.target.checked)
                  }
                />
                <Typography>Somente com orçamento</Typography>
                {loadingOptions && (
                  <CircularProgress size={18} sx={{ ml: 2 }} />
                )}
              </Box>
            </Grid>
          </Grid>
        </Card>

        {!hasUserModifiedDates && dateRangeInfo && (
          <Box
            sx={{
              backgroundColor: "#EBF5FF",
              border: "1px solid #BFDBFE",
              borderRadius: 1,
              p: "11px 16px",
              mb: "20px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <FaInfoCircle size={18} color="#1E40AF" />
            <Typography
              sx={{
                color: "#1E3A8A",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Exibindo projetos dos últimos{" "}
              <strong>{dateRangeInfo.days} dias</strong> (
              {dateRangeInfo.startFormatted} – {dateRangeInfo.endFormatted}
              ). Para ampliar o período, utilize os filtros de data.
            </Typography>
          </Box>
        )}

        <InsightsPanel summary={summary} cells={cellData} />

        <AdoptionKpis summary={summary} isLoading={loadingSummary} />

        <StaleProjectsCard
          summary={summary}
          isLoading={loadingSummary}
          active={staleOnly}
          onToggle={() => setStaleOnly((current) => !current)}
        />

        <AdoptionEvolutionChart data={monthlyData} isLoading={loadingMonthly} />

        <Grid container spacing={2} mb="18px">
          <Grid item xs={12} md={7}>
            <AdoptionByCellChart data={cellData} isLoading={loadingCells} />
          </Grid>
          <Grid item xs={12} md={5}>
            <TopClientsChart data={clientData} isLoading={loadingClients} />
          </Grid>
        </Grid>

        <Card sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize={15} fontWeight={700}>
                Projetos
              </Typography>
              <Typography color="#64748B" fontSize={13}>
                {totalRows} projeto(s)
              </Typography>
              {staleOnly && (
                <Chip
                  size="small"
                  label={`Sem orçamento há +${summary?.staleDays ?? 7} dias`}
                  onDelete={() => setStaleOnly(false)}
                  sx={{
                    fontWeight: 700,
                    color: "#9A3412",
                    backgroundColor: "#FFEDD5",
                  }}
                />
              )}
            </Box>
            <TextField
              placeholder="Pesquisar cliente ou evento..."
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              sx={{ width: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <TableContainer
            component={Paper}
            sx={{ boxShadow: "none", border: "none" }}
          >
            <Table {...getTableProps()}>
              <TableHead>
                {headerGroups.map((headerGroup) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column: any) => (
                      <TableCell
                        {...column.getHeaderProps(
                          column.getSortByToggleProps()
                        )}
                        sx={{
                          backgroundColor: "#F8FAFC",
                          color: "#64748B",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "uppercase",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {column.render("Header")}
                          <SortIcon column={column} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {hasProjectsError ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        alignItems="center"
                      >
                        <Typography color="error">
                          Não foi possível carregar os projetos.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : isInitialProjectsLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : rows.length ? (
                  rows.map((row) => {
                    prepareRow(row);
                    return (
                      <TableRow {...row.getRowProps()} hover>
                        {row.cells.map((cell) => (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      Nenhum projeto encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            ref={loaderRef}
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            {isLoadingNextPage && <CircularProgress size={24} />}
            {!loadingProjects && !hasMore && pageItems.length > 0 && (
              <Typography color="text.secondary" fontSize={13}>
                Fim da lista
              </Typography>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
